import { RefObject, useCallback, useEffect, useRef, useState } from "react";

export type Dir = "left" | "right";

export type UseTabScrollerOptions = {
  // Single-click scroll
  step?: number; // px per click (default 160)
  smooth?: boolean; // smooth behavior on step click (default true)

  // Press-and-hold
  intervalMs?: number; // tick rate for hold (default 16 ~60fps)
  baseStep?: number; // starting px per tick (default 20)
  accelEveryMs?: number; // how often to accelerate (default 400)
  accelMult?: number; // multiply step each accel tick (default 1.15)
  maxStep?: number; // clamp step (default 120)
};

const DEFAULTS: Required<UseTabScrollerOptions> = {
  step: 160,
  smooth: true,
  intervalMs: 16,
  baseStep: 20,
  accelEveryMs: 400,
  accelMult: 1.15,
  maxStep: 120,
};

export function useTabScroller(
  scrollerRef: RefObject<HTMLUListElement | null>,
  options: UseTabScrollerOptions = {},
) {
  const opts = { ...DEFAULTS, ...options };

  // Edge/overflow state for disabling arrows etc.
  const [hasOverflow, setHasOverflow] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  // ---- hold-to-scroll plumbing ----
  const holdTimerRef = useRef<number | null>(null);
  const accelTimerRef = useRef<number | null>(null);
  const holdStepRef = useRef<number>(opts.baseStep);
  const holdDirRef = useRef<Dir>("right");

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const overflow = el.scrollWidth > el.clientWidth + 1;
    setHasOverflow(overflow);
    const start = el.scrollLeft <= 1;
    const end = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
    setAtStart(start);
    setAtEnd(end);
  }, []);

  // Single step scroll
  const scrollByStep = (dir: Dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const delta = dir === "left" ? -opts.step : opts.step;
    el.scrollBy({ left: delta, behavior: opts.smooth ? "smooth" : "auto" });
  };

  // Press & Hold
  const startHold = (dir: Dir) => {
    const el = scrollerRef.current;
    if (!el) return;

    // Clear any previous timers
    if (holdTimerRef.current) clearInterval(holdTimerRef.current);
    if (accelTimerRef.current) clearInterval(accelTimerRef.current);

    holdDirRef.current = dir;
    holdStepRef.current = opts.baseStep;

    // Tick
    holdTimerRef.current = window.setInterval(() => {
      const e = scrollerRef.current;
      if (!e) return;
      e.scrollLeft +=
        holdDirRef.current === "left"
          ? -holdStepRef.current
          : holdStepRef.current;
    }, opts.intervalMs);

    // Acceleration
    accelTimerRef.current = window.setInterval(() => {
      holdStepRef.current = Math.min(
        opts.maxStep,
        holdStepRef.current * opts.accelMult,
      );
    }, opts.accelEveryMs);

    // Stop on pointer up/cancel anywhere
    const stopOnce = () => stopHold();
    window.addEventListener("mouseup", stopOnce, { once: true });
    window.addEventListener("touchend", stopOnce, { once: true });
    window.addEventListener("touchcancel", stopOnce, { once: true });
  };
  const stopHold = () => {
    if (holdTimerRef.current != null) {
      clearInterval(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    if (accelTimerRef.current != null) {
      clearInterval(accelTimerRef.current);
      accelTimerRef.current = null;
    }
  };

  // Keep edge flags in sync
  useEffect(() => {
    updateEdges();
    const el = scrollerRef.current;
    if (!el) return;

    const onScroll = () => updateEdges();

    el.addEventListener("scroll", onScroll);
    const ro = new ResizeObserver(() => updateEdges());
    ro.observe(el);

    return () => {
      el.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [scrollerRef, updateEdges]);

  // Safety: clear hold timers on unmount
  useEffect(() => stopHold, []);

  return {
    // actions
    scrollByStep,
    startHold,
    stopHold,

    // state (optional helpers for UI)
    hasOverflow,
    atStart,
    atEnd,

    // you can call this if content changes (optional)
    updateEdges,
  };
}
